import * as THREE from 'three'
import { trackGeoStartLength } from '../../config/constants'
import { applyHeightFogToObject } from '../../materials/heightFog'
import type { TrackController } from './trackController'
import { markForDisposal } from '../../../utils/threeDisposal'

type SourceBorderPart = {
    geometry: THREE.BufferGeometry
    material: THREE.Material
}

type BorderAssetsByRegion = Record<1 | 2 | 3 | 4, THREE.Group>

export function createBorderGeo(
    borderAssetsByRegion: BorderAssetsByRegion,
    trackController: TrackController,
) {
    const borderGroup = new THREE.Group()
    borderGroup.name = 'BorderTrackGroup'

    for (const region of [1, 2, 3, 4] as const) {
        const sourcePart = collectSourceBorder(borderAssetsByRegion[region])
        const mesh = buildBorderRegionMesh(
            sourcePart,
            region,
            trackController,
        )
        sourcePart.geometry.dispose()

        if (!mesh) {
            continue
        }

        mesh.name = `BorderRegion${region}`
        borderGroup.add(mesh)
    }

    applyHeightFogToObject(borderGroup)

    return borderGroup
}

function collectSourceBorder(borderAsset: THREE.Group): SourceBorderPart {
    let border: SourceBorderPart | null = null

    borderAsset.updateMatrixWorld(true)
    borderAsset.traverse((child) => {
        if (!(child instanceof THREE.Mesh)) {
            return
        }

        const geometry = child.geometry.index
            ? child.geometry.toNonIndexed()
            : child.geometry.clone()
        geometry.applyMatrix4(child.matrixWorld)
        geometry.computeBoundingBox()

        const material = Array.isArray(child.material)
            ? child.material[0]
            : child.material

        border = { geometry, material }
    })

    if (!border) {
        throw new Error('Border asset needs at least one mesh.')
    }

    return border
}

function buildBorderRegionMesh(
    sourcePart: SourceBorderPart,
    region: 1 | 2 | 3 | 4,
    trackController: TrackController,
) {
    if (!sourcePart.geometry.boundingBox) {
        throw new Error('Border mesh is missing its bounding box.')
    }

    const bounds = sourcePart.geometry.boundingBox
    const tileLength = bounds.max.z - bounds.min.z
    const usableTrackLength = Math.max(
        trackController.getTrackLength() - trackGeoStartLength,
        0,
    )
    const tileCount = Math.max(1, Math.floor(usableTrackLength / tileLength))
    const geometry = createBorderRegionGeometry(
        sourcePart.geometry,
        bounds.min.z,
        tileLength,
        tileCount,
        region,
        trackController,
    )

    if (geometry.getAttribute('position').count === 0) {
        return null
    }

    const mesh = new THREE.Mesh(geometry, sourcePart.material)
    mesh.castShadow = false
    mesh.receiveShadow = false
    return mesh
}

function createBorderRegionGeometry(
    source: THREE.BufferGeometry,
    sourceMinZ: number,
    tileLength: number,
    tileCount: number,
    region: 1 | 2 | 3 | 4,
    trackController: TrackController,
) {
    const positionAttribute = source.getAttribute('position')
    const normalAttribute = source.getAttribute('normal')
    const uvAttribute = source.getAttribute('uv')
    const positions: number[] = []
    const normals: number[] = []
    const uvs: number[] = []

    const localPosition = new THREE.Vector3()
    const localNormal = new THREE.Vector3()
    const worldPosition = new THREE.Vector3()
    const worldNormal = new THREE.Vector3()

    for (let tileIndex = 0; tileIndex < tileCount; tileIndex += 1) {
        const tileDistance = trackGeoStartLength + tileIndex * tileLength
        const samplePosition = tileDistance + tileLength * 0.5
        const regionState = trackController.getTrackRegionState(samplePosition)

        if (regionState.activeTransition || regionState.activeRegion !== region) {
            continue
        }

        for (let vertexIndex = 0; vertexIndex < positionAttribute.count; vertexIndex += 1) {
            localPosition.fromBufferAttribute(positionAttribute, vertexIndex)

            const frame = trackController.frameAtPosition(
                tileDistance + localPosition.z - sourceMinZ,
            )

            worldPosition
                .copy(frame.position)
                .addScaledVector(frame.trackRight, localPosition.x)
                .addScaledVector(frame.up, localPosition.y)

            positions.push(worldPosition.x, worldPosition.y, worldPosition.z)

            localNormal.fromBufferAttribute(normalAttribute, vertexIndex).normalize()

            worldNormal
                .copy(frame.trackRight)
                .multiplyScalar(localNormal.x)
                .addScaledVector(frame.up, localNormal.y)
                .addScaledVector(frame.tangent, localNormal.z)
                .normalize()

            normals.push(worldNormal.x, worldNormal.y, worldNormal.z)
            uvs.push(
                uvAttribute.getX(vertexIndex),
                uvAttribute.getY(vertexIndex) + tileIndex,
            )
        }
    }

    const geometry = new THREE.BufferGeometry()
    markForDisposal(geometry)
    geometry.setAttribute(
        'position',
        new THREE.Float32BufferAttribute(positions, 3),
    )
    geometry.setAttribute(
        'normal',
        new THREE.Float32BufferAttribute(normals, 3),
    )
    geometry.setAttribute(
        'uv',
        new THREE.Float32BufferAttribute(uvs, 2),
    )
    return geometry
}
